import type { WakeOrbSize, WakeOrbState } from "./types";

/** Rendered box in CSS pixels. 1.5× the Figma artboard (`small` 64, `medium` 88). */
export const SIZE_PX: Record<WakeOrbSize, number> = {
  small: 96,
  medium: 132,
};

/** Cap the backing store so a 3x phone doesn't render 4x the pixels for free. */
export const MAX_DPR = 2;

/**
 * Geometry as ratios of the box, measured off the exported Figma SVG for the
 * listening orb (node `20168:41714`, 83.7931 square):
 *   `<circle cx=41.8966 cy=41.8966 r=40.8966 fill="#081B1D" fill-opacity=".7"
 *            stroke="#EBF8F9" stroke-width="2" />`
 * Keeping these as ratios is what lets `small` and `medium` share one draw path.
 */
export const RING_RADIUS_RATIO = 40.8966 / 83.7931; // 0.48807
export const RING_WIDTH_RATIO = 2 / 83.7931; //        0.02387
/** Figma's `fill-opacity` on the body circle. */
export const BODY_OPACITY = 0.5;

/**
 * The speaking texture's image fill overhangs its ellipse — Figma inset
 * `[0 -7.5% -10% -2.5%]` on the 80px node (frame `20168:41427`), i.e. a 110%
 * box nudged left. Reproduced exactly so the cloud crops as designed.
 */
export const TEXTURE_SCALE = 1.1;
export const TEXTURE_OFFSET_X_RATIO = -0.025;
export const TEXTURE_OFFSET_Y_RATIO = 0;

/**
 * Interior fluid — see `fluid.ts`. Painted per-pixel into an offscreen buffer
 * and upscaled into the orb's clip. It flows; it never rotates.
 */
/** Offscreen grid, derived from the orb size then clamped. */
export const FLUID_GRID_RATIO = 0.62;
export const FLUID_GRID_MIN = 32;
export const FLUID_GRID_MAX = 64;
/**
 * The fluid drifts slowly, so repainting the buffer at 30fps is invisible while
 * halving the per-pixel cost. Compositing still runs every frame, so the halo
 * pulse and breathing stay at full rate.
 */
export const FLUID_REPAINT_FPS = 30;
/** Domain-warp strength at `flow = 1`. */
export const FLUID_WARP = 0.6;
/** Contrast stretch applied about the noise field's mean. */
export const FLUID_CONTRAST = 1.9;
/** Where `glow` sits on the `body -> glow -> ring` ramp. */
export const FLUID_RAMP_MID = 0.62;
/** Master clock scale for the integrated flow time. */
export const FLUID_TIME_SCALE = 0.55;

/** How far the halo reaches past the rim, as a ratio of the orb box. */
export const HALO_REACH_RATIO = 0.16;
/** How much louder audio swells the halo. */
export const HALO_LEVEL_SWELL = 0.4;

/**
 * How wide the orb actually *paints* at full audio swell, as a ratio of its
 * box — the rim plus the halo's reach at full swell, on both sides.
 *
 * The box only reserves `SIZE_PX`, so the halo deliberately bloats past it.
 * Layout that places anything hard up against the orb should reserve this
 * instead, or the bloom will wash over it. Comes out just under 1.43, i.e.
 * ~189px around a `medium` orb.
 */
export const BLOOM_RATIO =
  2 * (RING_RADIUS_RATIO + HALO_REACH_RATIO * (1 + HALO_LEVEL_SWELL));

/**
 * Off-centre depth shading, so the disc reads as a lit sphere rather than a
 * flat circle. Light sits up and to the right, matching the reference.
 * Pure black at varying alpha — introduces no new colour.
 */
export const DEPTH_LIGHT_X_RATIO = 0.2;
export const DEPTH_LIGHT_Y_RATIO = -0.24;
export const DEPTH_EDGE_ALPHA = 0.5;

/**
 * Mic glyph box as a ratio of the orb, from Figma node `20172:41823`
 * (32.5013 x 35.6897 at x=25.54 y=24.25 inside 83.7931 -> optically centered).
 * The glyph is deliberately not square; do not collapse these to one number.
 */
export const MIC_WIDTH_RATIO = 32.5013 / 83.7931; //  0.38788
export const MIC_HEIGHT_RATIO = 35.6897 / 83.7931; // 0.42593

/**
 * The Figma circle nearly fills its own frame (`r = 0.488 * size`), so there is
 * no room *inside* the designed box for an outward glow. The canvas is drawn
 * this much larger than the box and overhangs it, while layout still reserves
 * only `SIZE_PX`. `WakeOrb.css` must keep its canvas inset in sync:
 * `inset: -(CANVAS_BLEED - 1) / 2` as a percentage — i.e. `-40%` at 1.8.
 * Sized to fit `HALO_REACH_RATIO` at full swell.
 */
export const CANVAS_BLEED = 1.8;

export type StateFeel = {
  /** Multiplier on `BODY_OPACITY` for the dark disc. */
  body: number;
  /** Ring stroke alpha. */
  ring: number;
  /** Speaking texture alpha. */
  texture: number;
  /**
   * Strength of the spherical depth shading, 0..1. Keep this low for states
   * whose artwork already carries its own rim shading — shading it twice is
   * what made speaking read too dark.
   */
  depth: number;
  /** Alpha of the interior fluid layer. */
  fluid: number;
  /** Fluid exposure, 0..1 — how much light the field reveals. */
  fluidBright: number;
  /** Fluid domain-warp strength, 0..1. */
  fluidFlow: number;
  /**
   * Shaping exponent on the fluid field — how much of it reads as light.
   * 2 is the baseline: mid-tones stay dark and only peaks show as thin wisps.
   * Below 1 opens the bright end into near-white. Lower = more white.
   */
  fluidGamma: number;
  /** Fluid clock rate. 0 freezes the interior. */
  fluidSpeed: number;
  /** Base alpha of the outer halo. */
  halo: number;
  /** Depth of the halo's glow pulse, 0 = steady. */
  haloPulse: number;
  /**
   * Blends the halo's colour from `glow` toward `success`, 0..1. Eased like
   * every other feel value, so confirming a request warms the halo from teal
   * to green rather than swapping it in one frame.
   */
  haloSuccess: number;
  /** Pulse / breathe frequency in Hz. */
  rateHz: number;
  /** Breathing scale amplitude, as a ratio (0.015 = +-1.5%). */
  breathe: number;
  /** How much `level` swells the halo and speeds the interior. */
  levelGain: number;
};

/**
 * Per-state targets. The renderer eases *between* these rather than switching,
 * so a state change cross-fades instead of popping.
 *
 * Two independent animations, per the agreed design:
 *   - the OUTER halo *glows* — pulses in reach and alpha
 *   - the INTERIOR fluid *flows* inside the disc
 * Nothing rotates, in any state.
 *
 * Figma still owns the chrome: listening keeps the disc, 2px ring and mic from
 * frame `20168:41712`; speaking keeps the cloud texture from `20168:41427`.
 * `idle`, `connecting` and `processing` have no Figma node yet — retune here
 * once design lands.
 */
export const STATE_FEEL: Record<WakeOrbState, StateFeel> = {
  dormant: {
    // Speaking's look, at rest: same light teal-blue interior and same fluid,
    // but no halo at all, no ring, and drifting slowly. `levelGain: 0` cuts it
    // off from the microphone entirely, so it never reacts to audio.
    body: 0,
    ring: 0,
    texture: 1,
    depth: 0,
    fluid: 2,
    fluidBright: 0.95,
    fluidFlow: 5,
    // Well under the 2 baseline, so the field's mid-tones climb past the glow
    // stop into near-white instead of only the peaks showing.
    fluidGamma: 0.9,
    fluidSpeed: 3,
    halo: 0,
    haloPulse: 0,
    haloSuccess: 0,
    rateHz: 0,
    breathe: 0.00,
    levelGain: 0,
  },
  idle: {
    body: 1,
    ring: 0,
    texture: 0,
    depth: 1,
    fluid: 0.9,
    fluidBright: 0.3,
    fluidFlow: 0.75,
    fluidGamma: 2,
    fluidSpeed: 0.35,
    halo: 0.2,
    haloPulse: 0.55,
    haloSuccess: 0,
    rateHz: 0.16,
    breathe: 0.015,
    levelGain: 0,
  },
  connecting: {
    // "Glowing": deep halo pulse, interior picking up pace.
    body: 1,
    ring: 0,
    texture: 0,
    depth: 1,
    fluid: 0.95,
    fluidBright: 0.55,
    fluidFlow: 0.9,
    fluidGamma: 2,
    fluidSpeed: 0.9,
    halo: 0.5,
    haloPulse: 1,
    haloSuccess: 0,
    rateHz: 0.5,
    breathe: 0.01,
    levelGain: 0,
  },
  listening: {
    body: 1,
    ring: 1,
    texture: 0,
    depth: 1,
    fluid: 1,
    fluidBright: 0.6,
    fluidFlow: 0.95,
    fluidGamma: 2,
    fluidSpeed: 0.8,
    halo: 0.4,
    haloPulse: 0.35,
    haloSuccess: 0,
    rateHz: 0.3,
    breathe: 0.006,
    levelGain: 1,
  },
  processing: {
    // Fast churn with no halo emphasis reads as working, not waiting.
    body: 1,
    ring: 0.5,
    texture: 0,
    depth: 1,
    fluid: 1,
    fluidBright: 0.55,
    fluidFlow: 1,
    fluidGamma: 2,
    fluidSpeed: 1.5,
    halo: 0.3,
    haloPulse: 0.3,
    haloSuccess: 0,
    rateHz: 0.7,
    breathe: 0,
    levelGain: 0,
  },
  speaking: {
    // Light teal-blue. Three things were dimming this: a dark disc showing
    // through a part-transparent texture, and the depth vignette on top of the
    // rim shading the artwork already has. So: no disc, texture at full
    // strength, and the fluid ramps from the lighter `--wake-orb-fluid-low`
    // (scoped in WakeOrb.css) rather than the near-black disc colour.
    body: 0,
    ring: 0,
    texture: 1,
    depth: 0.12,
    fluid: 0.6,
    fluidBright: 0.95,
    fluidFlow: 1,
    fluidGamma: 2,
    fluidSpeed: 1.1,
    halo: 0.5,
    haloPulse: 0.3,
    haloSuccess: 0,
    rateHz: 0.5,
    breathe: 0.012,
    levelGain: 1,
  },
  success: {
    // Figma `20183:57513`. The disc, its 1px inset highlight and the check are
    // DOM — see `.wake-orb__success` in WakeOrb.css, which reproduces the
    // 135deg gradient and the inset exactly where canvas would approximate.
    //
    // Everything the disc covers is therefore zero here. The halo is the one
    // canvas layer left running: it is the only part that shows outside the
    // disc, and `haloSuccess` recolours it to Figma's green so the orb keeps
    // the breathing glow the other states have instead of going inert.
    body: 0,
    ring: 0,
    texture: 0,
    depth: 0,
    fluid: 0,
    fluidBright: 0,
    fluidFlow: 0,
    fluidGamma: 2,
    fluidSpeed: 0,
    halo: 0.6,
    haloPulse: 0.45,
    haloSuccess: 1,
    rateHz: 0.28,
    // Must stay 0: the DOM disc can't breathe with the canvas, so any scale
    // here would show up as the halo sliding against a fixed rim.
    breathe: 0,
    levelGain: 0,
  },
  disabled: {
    body: 1,
    ring: 0.22,
    texture: 0,
    depth: 1,
    fluid: 0.8,
    fluidBright: 0.12,
    fluidFlow: 0.5,
    fluidGamma: 2,
    fluidSpeed: 0,
    halo: 0,
    haloPulse: 0,
    haloSuccess: 0,
    rateHz: 0,
    breathe: 0,
    levelGain: 0,
  },
};

/**
 * Whether a state animates forever, or settles to a still frame.
 *
 * The render loop runs while the current state has motion *or* while values are
 * still easing, then stops on its own. That means `disabled` costs nothing once
 * settled, but a transition *into* it still gets to play out instead of
 * freezing halfway.
 */
export const STATE_MOTION: Record<WakeOrbState, boolean> = {
  dormant: true,
  idle: true,
  connecting: true,
  listening: true,
  processing: true,
  speaking: true,
  // The disc is static, but its halo keeps breathing.
  success: true,
  // Static by design — nothing to animate.
  disabled: false,
};

/** Below this difference an eased value counts as arrived. */
export const SETTLE_EPSILON = 0.002;

/** Seconds to cross-fade visual properties on a state change. */
export const STATE_TAU = 0.28;
/** Asymmetric level smoothing: quick to rise, slow to fall, so peaks read. */
export const LEVEL_ATTACK_TAU = 0.05;
export const LEVEL_RELEASE_TAU = 0.28;

export const STATE_LABEL: Record<WakeOrbState, string> = {
  dormant: "Assistant dormant",
  idle: "Assistant idle",
  connecting: "Assistant connecting",
  listening: "Assistant listening",
  processing: "Assistant thinking",
  speaking: "Assistant speaking",
  success: "Request confirmed",
  disabled: "Assistant unavailable",
};
